import { useMemo, useState } from "react"
import { SubModal } from "@/components/ui/sub-modal"
import { formatOperatorName } from "@/utils/format-operator-name"
import { getTimeAgo } from "@/utils/time-ago"
import { Clock, FileText, History, User, CheckCircle, XCircle, MessageSquare, Mail, Loader2, Paperclip, Eye, Layers, Store, ArrowUp, ArrowDown } from "lucide-react"
import { Switch } from "@/components/ui/switch"
import type { Divergence, Nsu } from "../../types"
import { ItemType } from "../../types/shared"
import { type AuditLogEvent, type AuditLogResponse, type AuditFieldChange } from "@/services/conciliation/audit-log"
import { AUDIT_ACTION_TYPES, AUDIT_EVENT_TYPES } from "@/constants/conciliation/audit.constants"

interface EvidenceHistoryEntry {
  field: string
  reason: string
  newValue: string
  oldValue: string
  changedAt: string
  changedBy: string
  isRejection?: boolean
  actionType?: 'approval' | 'rejection' | 'response' | 'store_justification' | 'store_response' | 'email_solicitation' | 'email_resent' | 'file_upload' | 'email_opened' | 'other'
  responseNumber?: number
  rejectionNumber?: number
  changes?: AuditFieldChange[]
  message?: string
}

interface StoreSession {
  id: string
  sessionNumber: number
  startTime: Date
  endTime: Date
  entries: EvidenceHistoryEntry[]
}

type HistoryItem =
  | { type: 'entry'; entry: EvidenceHistoryEntry; index: number }
  | { type: 'session'; session: StoreSession }

// Ações que são da loja (agrupáveis em sessão)
const STORE_ACTION_TYPES = ['email_opened', 'file_upload', 'store_response', 'store_justification', 'response'] as const

const isStoreAction = (actionType: EvidenceHistoryEntry['actionType']): boolean => {
  return STORE_ACTION_TYPES.includes(actionType as typeof STORE_ACTION_TYPES[number])
}

const SESSION_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutos

interface IndividualEvidenceHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  item: {
    type: ItemType
    id: number
    name?: string
    number?: string
    review_id?: number
  } & (Divergence | Nsu)
  auditData?: AuditLogResponse | null
  isAuditLoading?: boolean
}

const determineActionType = (
  field: string,
  newValue: string,
  action?: string
): EvidenceHistoryEntry['actionType'] => {
  if (action === AUDIT_ACTION_TYPES.ANALYST_APPROVED_EVIDENCE) return 'approval'
  if (action === AUDIT_ACTION_TYPES.ANALYST_REJECTED_EVIDENCE) return 'rejection'
  if (action === AUDIT_ACTION_TYPES.EMAIL_SENT) return 'email_solicitation'
  if (action === AUDIT_ACTION_TYPES.EMAIL_RESENT) return 'email_resent'
  if (action === AUDIT_ACTION_TYPES.STORE_UPLOADED_FILES) return 'file_upload'
  if (action === AUDIT_ACTION_TYPES.STORE_OPENED_EMAIL) return 'email_opened'

  if (field === 'status') {
    if (newValue === 'APPROVED') return 'approval'
    if (newValue === 'REJECTED_WAITING_FOR_RESPOND') return 'rejection'
    if (newValue === 'RESPONDED') return 'response'
  }
  if (field === 'store_justification') return 'store_justification'
  if (field === 'store_response') return 'store_response'
  if (field === 'email_solicitation') return 'email_solicitation'
  if (field === 'email_resent') return 'email_resent'
  if (field === 'file_uploaded') return 'file_upload'
  if (field === 'email_opened') return 'email_opened'

  return 'other'
}

const convertAuditEventToHistoryEntry = (event: AuditLogEvent): EvidenceHistoryEntry | null => {
  const payload = event.event_payload
  const action = payload.action as string

  if (payload.changes && payload.changes.length > 0) {
    const statusChange = payload.changes.find(c => c.field === 'status')
    const primaryField = statusChange?.field || payload.changes[0].field
    const primaryNewValue = statusChange?.to || payload.changes[0].to || ''
    const actionType = determineActionType(primaryField, primaryNewValue, action)

    const isRejection = actionType === 'rejection' ||
      payload.message?.toLowerCase().includes("rejeição") ||
      payload.message?.toLowerCase().includes("rejeitado") ||
      payload.message?.toLowerCase().includes("recusa")

    return {
      field: primaryField,
      reason: payload.message || action || '',
      newValue: primaryNewValue,
      oldValue: statusChange?.from || payload.changes[0].from || '',
      changedAt: event.created_at,
      changedBy: event.email || 'Sistema',
      isRejection,
      actionType,
      changes: payload.changes,
      message: payload.message,
    }
  }

  let field = payload.field || 'status'
  let oldValue = payload.from || ''
  let newValue = payload.to || ''
  let reason = payload.reason || payload.action || ''

  if (event.event_type === AUDIT_EVENT_TYPES.ACTION_EXECUTED) {
    switch (action) {
      case AUDIT_ACTION_TYPES.EMAIL_SENT:
        field = 'email_solicitation'
        reason = payload.metadata?.message || 'Solicitação de evidência enviada'
        newValue = 'Enviado'
        break
      case AUDIT_ACTION_TYPES.EMAIL_RESENT:
        field = 'email_resent'
        reason = payload.metadata?.message || 'Email de solicitação reenviado'
        newValue = 'Reenviado'
        break
      case AUDIT_ACTION_TYPES.STORE_UPLOADED_FILES:
        field = 'file_uploaded'
        reason = payload.metadata?.message || 'Loja anexou arquivo'
        newValue = 'Arquivo enviado'
        break
      case AUDIT_ACTION_TYPES.STORE_FINALIZED_CONCILIATION:
        field = 'status'
        reason = 'Loja respondeu solicitação de email de conciliação'
        newValue = 'RESPONDED'
        break
      case AUDIT_ACTION_TYPES.ANALYST_REJECTED_EVIDENCE:
        field = 'status'
        reason = 'Analista recusou evidência enviada pela loja'
        newValue = 'REJECTED_WAITING_FOR_RESPOND'
        break
      case AUDIT_ACTION_TYPES.ANALYST_APPROVED_EVIDENCE:
        field = 'status'
        reason = 'Analista aprovou evidência'
        newValue = 'APPROVED'
        break
      case AUDIT_ACTION_TYPES.STORE_OPENED_EMAIL:
        field = 'email_opened'
        reason = payload.message || 'Loja abriu o link do email de solicitação'
        newValue = 'Visualizado'
        break
      default:
        reason = payload.metadata?.message || action
    }
  }

  const actionType = determineActionType(field, newValue, action)
  const isRejection = actionType === 'rejection' ||
    reason?.toLowerCase().includes("rejeição") ||
    reason?.toLowerCase().includes("rejeitado") ||
    reason?.toLowerCase().includes("recusa")

  return {
    field,
    reason,
    newValue,
    oldValue,
    changedAt: event.created_at,
    changedBy: event.email || 'Sistema',
    isRejection,
    actionType,
  }
}

export const IndividualEvidenceHistoryModal = ({ isOpen, onClose, item, auditData, isAuditLoading }: IndividualEvidenceHistoryModalProps) => {
  const [groupBySessions, setGroupBySessions] = useState(false)
  const [sessionSortOrder, setSessionSortOrder] = useState<Record<string, 'asc' | 'desc'>>({})

  const getStatusDisplay = (status: string): string => {
    const statusMap: Record<string, string> = {
      'PENDING_ANSWER': 'Aguardando Resposta',
      'APPROVED': 'Aprovado',
      'REJECTED': 'Rejeitado',
      'REJECTED_WAITING_FOR_RESPOND': 'Rejeitado - Aguardando Resposta',
      'RESPONDED': 'Respondido'
    }
    return statusMap[status] || status
  }

  const getActionType = (entry: any): EvidenceHistoryEntry['actionType'] => {
    if (entry.field === 'status') {
      if (entry.new_value === 'APPROVED') return 'approval'
      if (entry.new_value === 'REJECTED_WAITING_FOR_RESPOND') return 'rejection'
      if (entry.new_value === 'RESPONDED') return 'response'
    }
    if (entry.field === 'store_justification') return 'store_justification'
    if (entry.field === 'store_response') return 'store_response'
    if (entry.field === 'email_solicitation') return 'email_solicitation'
    if (entry.field === 'file_uploaded') return 'file_upload'
    return 'other'
  }

  const formatHistoryDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const validateHistoryEntry = (entry: any): boolean => {
    return (
      typeof entry === 'object' &&
      typeof entry.field === 'string' &&
      typeof entry.reason === 'string' &&
      typeof entry.changed_at === 'string' &&
      entry.old_value !== undefined &&
      entry.new_value !== undefined
    )
  }

  const evidenceHistory = useMemo((): EvidenceHistoryEntry[] => {
    const history: EvidenceHistoryEntry[] = []

    if (!item) return history

    if (auditData?.events && auditData.events.length > 0) {
      auditData.events.forEach((event) => {
        const entry = convertAuditEventToHistoryEntry(event)
        if (entry) history.push(entry)
      })
    }

    if (history.length === 0) {
      const historyData = (item as any).review_notes_history || []

      historyData.forEach((entry: any) => {
        if (!validateHistoryEntry(entry)) return

        const actionType = getActionType(entry)
        const isRejection = actionType === 'rejection' ||
          entry.reason?.toLowerCase().includes("rejeição") ||
          entry.reason?.toLowerCase().includes("rejeitado") ||
          entry.reason?.toLowerCase().includes("recusa")

        history.push({
          field: entry.field,
          reason: entry.reason,
          newValue: entry.new_value,
          oldValue: entry.old_value,
          changedAt: entry.changed_at,
          changedBy: entry.changed_by || entry.changedBy || "Sistema",
          isRejection,
          actionType,
          responseNumber: entry.response_number,
          rejectionNumber: entry.rejection_number,
        })
      })
    }

    return history.sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
  }, [item, auditData])

  // Agrupar ações da loja em sessões (15 min)
  const groupedHistory = useMemo((): HistoryItem[] => {
    if (!groupBySessions) {
      return evidenceHistory.map((entry, index) => ({ type: 'entry' as const, entry, index }))
    }

    const items: HistoryItem[] = []
    const sessions: StoreSession[] = []
    let currentSession: StoreSession | null = null

    // Ordenar por data crescente para agrupar corretamente
    const sortedHistory = [...evidenceHistory].sort(
      (a, b) => new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime()
    )

    sortedHistory.forEach((entry) => {
      if (isStoreAction(entry.actionType)) {
        const entryTime = new Date(entry.changedAt)

        if (currentSession) {
          const timeDiff = entryTime.getTime() - currentSession.endTime.getTime()

          if (timeDiff <= SESSION_TIMEOUT_MS) {
            currentSession.entries.push(entry)
            currentSession.endTime = entryTime
          } else {
            sessions.push(currentSession)
            currentSession = {
              id: `session-${sessions.length}`,
              sessionNumber: sessions.length + 1,
              startTime: entryTime,
              endTime: entryTime,
              entries: [entry]
            }
          }
        } else {
          currentSession = {
            id: `session-${sessions.length}`,
            sessionNumber: sessions.length + 1,
            startTime: entryTime,
            endTime: entryTime,
            entries: [entry]
          }
        }
      } else {
        if (currentSession) {
          sessions.push(currentSession)
          currentSession = null
        }
      }
    })

    if (currentSession) {
      sessions.push(currentSession)
    }

    // Reconstruir a timeline com sessões
    let globalIndex = 0
    const sortedHistoryDesc = [...evidenceHistory]

    sortedHistoryDesc.forEach((entry) => {
      if (isStoreAction(entry.actionType)) {
        const session = sessions.find(s =>
          s.entries.some(e => e.changedAt === entry.changedAt && e.actionType === entry.actionType)
        )

        if (session && !items.some(i => i.type === 'session' && i.session.id === session.id)) {
          items.push({ type: 'session', session })
        }
      } else {
        items.push({ type: 'entry', entry, index: globalIndex })
      }
      globalIndex++
    })

    return items
  }, [evidenceHistory, groupBySessions])

  // Contagem de sessões para exibir
  const sessionCount = useMemo(() => {
    return groupedHistory.filter(item => item.type === 'session').length
  }, [groupedHistory])

  if (!item) return null

  const itemName = item.type === ItemType.DIVERGENCE ? (item as Divergence).name : (item as Nsu).number
  const itemType = item.type === ItemType.DIVERGENCE ? "Divergência" : "NSU"

  const getActionConfig = (actionType: EvidenceHistoryEntry['actionType'], isRejection?: boolean) => {
    if (isRejection || actionType === 'rejection') {
      return {
        icon: <XCircle className="w-4 h-4" />,
        label: 'Rejeição',
        dotColor: 'bg-amber-500',
        iconColor: 'text-amber-600',
        cardBg: 'bg-amber-50/50',
        cardBorder: 'border-amber-100',
      }
    }

    switch (actionType) {
      case 'approval':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          label: 'Aprovação',
          dotColor: 'bg-emerald-500',
          iconColor: 'text-emerald-600',
          cardBg: 'bg-emerald-50/50',
          cardBorder: 'border-emerald-100',
        }
      case 'response':
        return {
          icon: <MessageSquare className="w-4 h-4" />,
          label: 'Resposta',
          dotColor: 'bg-sky-500',
          iconColor: 'text-sky-600',
          cardBg: 'bg-sky-50/50',
          cardBorder: 'border-sky-100',
        }
      case 'store_justification':
        return {
          icon: <MessageSquare className="w-4 h-4" />,
          label: 'Justificativa',
          dotColor: 'bg-violet-400',
          iconColor: 'text-violet-600',
          cardBg: 'bg-violet-50/50',
          cardBorder: 'border-violet-100',
        }
      case 'file_upload':
      case 'store_response':
        return {
          icon: <Paperclip className="w-4 h-4" />,
          label: 'Anexo',
          dotColor: 'bg-slate-400',
          iconColor: 'text-slate-600',
          cardBg: 'bg-slate-50/50',
          cardBorder: 'border-slate-100',
        }
      case 'email_solicitation':
        return {
          icon: <Mail className="w-4 h-4" />,
          label: 'Solicitação',
          dotColor: 'bg-indigo-400',
          iconColor: 'text-indigo-600',
          cardBg: 'bg-indigo-50/50',
          cardBorder: 'border-indigo-100',
        }
      case 'email_resent':
        return {
          icon: <Mail className="w-4 h-4" />,
          label: 'Reenvio',
          dotColor: 'bg-indigo-400',
          iconColor: 'text-indigo-600',
          cardBg: 'bg-indigo-50/50',
          cardBorder: 'border-indigo-100',
        }
      case 'email_opened':
        return {
          icon: <Eye className="w-4 h-4" />,
          label: 'Visualização',
          dotColor: 'bg-teal-400',
          iconColor: 'text-teal-600',
          cardBg: 'bg-teal-50/50',
          cardBorder: 'border-teal-100',
        }
      default:
        return {
          icon: <FileText className="w-4 h-4" />,
          label: 'Atualização',
          dotColor: 'bg-gray-400',
          iconColor: 'text-gray-500',
          cardBg: 'bg-gray-50',
          cardBorder: 'border-gray-100',
        }
    }
  }

  const formatFieldName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      'status': 'Status',
      'review_notes': 'Motivo',
      'store_justification': 'Justificativa',
      'store_response': 'Resposta',
      'file_uploaded': 'Arquivo',
    }
    return fieldNames[field] || field
  }

  const formatValue = (field: string, value: any): string => {
    if (field === 'status') return getStatusDisplay(value)
    if (value === null || value === undefined) return '-'
    return String(value)
  }

  const getSessionSortOrder = (sessionId: string): 'asc' | 'desc' => {
    return sessionSortOrder[sessionId] || 'asc'
  }

  const toggleSessionSortOrder = (sessionId: string) => {
    setSessionSortOrder(prev => ({
      ...prev,
      [sessionId]: prev[sessionId] === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getSortedSessionEntries = (session: StoreSession): EvidenceHistoryEntry[] => {
    const order = getSessionSortOrder(session.id)
    return [...session.entries].sort((a, b) => {
      const timeA = new Date(a.changedAt).getTime()
      const timeB = new Date(b.changedAt).getTime()
      return order === 'asc' ? timeA - timeB : timeB - timeA
    })
  }

  return (
    <SubModal isOpen={isOpen} onClose={onClose} title={`Histórico - ${itemName}`} maxWidth="max-w-2xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-900">{itemName}</span>
            <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded">{itemType}</span>
          </div>
          <div className="flex items-center gap-4">
            {!isAuditLoading && evidenceHistory.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  Sessões
                </span>
                <Switch
                  checked={groupBySessions}
                  onCheckedChange={setGroupBySessions}
                  className="scale-75"
                />
              </label>
            )}
            {isAuditLoading ? (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Carregando...
              </span>
            ) : (
              <span className="text-xs text-gray-500">
                {groupBySessions && sessionCount > 0
                  ? `${sessionCount} sessão(ões) · ${evidenceHistory.length} evento${evidenceHistory.length !== 1 ? 's' : ''}`
                  : `${evidenceHistory.length} evento${evidenceHistory.length !== 1 ? 's' : ''}`
                }
              </span>
            )}
          </div>
        </div>

        {isAuditLoading ? (
          <div className="text-center py-12 text-gray-500">
            <Loader2 className="w-10 h-10 mx-auto mb-3 text-gray-300 animate-spin" />
            <p className="text-sm">Carregando histórico...</p>
          </div>
        ) : evidenceHistory.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <History className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Nenhum histórico encontrado</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gray-200" />

            <div className="space-y-0">
              {groupedHistory.map((item, itemIndex) => {
                if (item.type === 'session') {
                  const { session } = item
                  const isFirstItem = itemIndex === 0

                  return (
                    <div key={session.id} className="relative pl-7 pb-5">
                      <div className={`absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full bg-cyan-500 border-2 border-white shadow-sm ${isFirstItem ? 'ring-2 ring-offset-1 ring-gray-200' : ''}`} />

                      <div className={`bg-cyan-50/50 border-2 border-cyan-200 ${isFirstItem ? 'shadow-sm' : ''} rounded-lg p-3 transition-colors`}>
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-cyan-600"><Store className="w-4 h-4" /></span>
                            <span className="text-sm font-medium text-gray-900">Sessão da Loja</span>
                            <span className="text-xs text-cyan-600 bg-cyan-100 px-1.5 py-0.5 rounded">
                              {session.entries.length} ação(ões)
                            </span>
                            <button
                              onClick={() => toggleSessionSortOrder(session.id)}
                              className="flex items-center gap-1 text-[10px] text-cyan-600 hover:text-cyan-800 bg-cyan-100 hover:bg-cyan-200 px-1.5 py-0.5 rounded transition-colors"
                              title={getSessionSortOrder(session.id) === 'asc' ? 'Mais antigo primeiro' : 'Mais recente primeiro'}
                            >
                              {getSessionSortOrder(session.id) === 'asc' ? (
                                <ArrowUp className="w-3 h-3" />
                              ) : (
                                <ArrowDown className="w-3 h-3" />
                              )}
                              {getSessionSortOrder(session.id) === 'asc' ? 'Antigo' : 'Recente'}
                            </button>
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatHistoryDate(session.startTime.toISOString())}
                          </span>
                        </div>

                        <div className="space-y-2 pl-2 border-l-2 border-cyan-200">
                          {getSortedSessionEntries(session).map((entry, entryIndex) => {
                            const config = getActionConfig(entry.actionType, entry.isRejection)

                            return (
                              <div key={entryIndex} className={`${config.cardBg} border ${config.cardBorder} rounded-md p-2`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={config.iconColor}>{config.icon}</span>
                                  <span className="text-xs font-medium text-gray-800">{config.label}</span>
                                  <span className="text-[10px] text-gray-400">
                                    {new Date(entry.changedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                {entry.reason && (
                                  <p className="text-xs text-gray-600">{entry.reason}</p>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )
                }

                const { entry, index } = item
                const config = getActionConfig(entry.actionType, entry.isRejection)
                const isFirst = itemIndex === 0

                return (
                  <div key={index} className="relative pl-7 pb-5">
                    <div className={`absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full ${config.dotColor} border-2 border-white shadow-sm ${isFirst ? 'ring-2 ring-offset-1 ring-gray-200' : ''}`} />

                    <div className={`${config.cardBg} border ${config.cardBorder} ${isFirst ? 'shadow-sm' : ''} rounded-lg p-3 transition-colors`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <span className={config.iconColor}>{config.icon}</span>
                          <span className="text-sm font-medium text-gray-900">{config.label}</span>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatHistoryDate(entry.changedAt)}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {formatOperatorName(entry.changedBy)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {getTimeAgo(entry.changedAt)}
                        </span>
                      </div>

                      {entry.reason && (
                        <p className="text-sm text-gray-700 mb-2">{entry.reason}</p>
                      )}

                      {entry.changes && entry.changes.length > 0 && entry.actionType !== 'email_opened' ? (
                        <div className="space-y-1.5 pt-2 border-t border-gray-200 mt-2">
                          {entry.changes
                            .filter(change => !['email_opened'].includes(change.field))
                            .map((change, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm">
                                <span className="text-gray-500 min-w-[80px]">{formatFieldName(change.field)}:</span>
                                {change.field === 'file_uploaded' ? (
                                  <span className="text-gray-900 flex items-center gap-1">
                                    <Paperclip className="w-3 h-3" />
                                    {change.to}
                                  </span>
                                ) : (
                                  <span className="text-gray-900">{formatValue(change.field, change.to)}</span>
                                )}
                              </div>
                            ))}
                        </div>
                      ) : entry.field === 'email_solicitation' ? (
                        <div className="pt-2 border-t border-gray-200 mt-2">
                          <p className="text-sm text-gray-600">Solicitação de evidência enviada para a loja</p>
                        </div>
                      ) : entry.field === 'email_resent' ? (
                        <div className="pt-2 border-t border-gray-200 mt-2">
                          <p className="text-sm text-gray-600">Email de solicitação reenviado para a loja</p>
                        </div>
                      ) : entry.newValue && !['email_solicitation', 'email_resent', 'email_opened'].includes(entry.field) ? (
                        <div className="pt-2 border-t border-gray-200 mt-2">
                          <div className="flex items-start gap-2 text-sm">
                            <span className="text-gray-500 min-w-[80px]">{formatFieldName(entry.field)}:</span>
                            <span className="text-gray-900">{formatValue(entry.field, entry.newValue)}</span>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </SubModal>
  )
}
